import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import MergeInvoiceModal from "src/main/Billing/Payments/MergeInvoiceModal"
import SettlePaymentModal from "src/main/Billing/Payments/SettlePaymentModal"
import { useMergeInvoice } from "src/main/Billing/Payments/hooks"
import ManageInvoiceModal from "src/main/Billing/PaymentsTableV2/ManageInvoiceModal"
import PaymentsDataTable from "src/main/Billing/PaymentsTableV2/PaymentsDataTable"
import { toLegacyInvoiceShape } from "src/main/Billing/PaymentsTableV2/legacyInvoiceShape"

import Button from "src/components/Button"
import { ADD_NEW_PAYMENT_METHOD } from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import {
  getPaymentMethodOptions,
  getWorkOrderInvoiceIds,
  getWorkOrderInvoices,
  updateInvoiceAutopay,
} from "src/api/Service/WorkOrders"

import useDatatable from "src/hooks/use_datatable"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import InvoiceAutopayFields from "./InvoiceAutopayFields"
import {
  INVOICE_AUTOPAY_SAVED,
  INVOICE_AUTOPAY_SAVE_FAILED,
  MANAGE_INVOICE_OPENED,
  MERGE_INVOICE_OPENED,
  REVERSE_INVOICE_OPENED,
  SETTLE_INVOICE_OPENED,
} from "./amplitude_events"

const WorkOrderPayments = ({ workOrder, workOrderSequentialId }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const tracker = useTracker()
  const queryClient = useQueryClient()
  const canManage = Boolean(workOrder.canManagePayments)

  const paymentsQueryKey = [
    "workOrderInvoices",
    marinaSlug,
    workOrderSequentialId,
  ]

  const table = useDatatable({
    queryKey: paymentsQueryKey,
    fetchFn: (params) =>
      getWorkOrderInvoices(marinaSlug, workOrderSequentialId, params),
    fetchAllIds: (params) =>
      getWorkOrderInvoiceIds(marinaSlug, workOrderSequentialId, params).then(
        (r) => r.ids
      ),
  })

  // Refetch the table and invalidate the WO detail query so the billing
  // summary stats (which live on the workOrder payload) reflect settle / merge
  // / autopay changes without a page reload.
  const refreshPayments = () => {
    queryClient.refetchQueries({ queryKey: paymentsQueryKey })
    queryClient.invalidateQueries([
      "workOrder",
      marinaSlug,
      workOrderSequentialId,
    ])
  }

  const { data: paymentMethodOptions } = useQuery({
    queryKey: ["workOrderPaymentMethods", marinaSlug, workOrderSequentialId],
    queryFn: () => getPaymentMethodOptions(marinaSlug, workOrderSequentialId),
    refetchOnWindowFocus: false,
  })

  const paymentMethods = useMemo(
    () => ({ cards: paymentMethodOptions ?? [] }),
    [paymentMethodOptions]
  )

  const [manageInvoice, setManageInvoice] = useState(null)
  const [settleInvoice, setSettleInvoice] = useState(null)

  // Per-invoice AutoPay editor state. Seeded from the opened row's paymentMethod
  // only for unsettled invoices — on a settled invoice that method is the card
  // that already paid it, not a scheduled future charge.
  const [autopayEnabled, setAutopayEnabled] = useState(false)
  const [autopayCardId, setAutopayCardId] = useState("")

  const openManage = (row) => {
    tracker.trackEvent(MANAGE_INVOICE_OPENED, {
      workOrderSequentialId,
      invoiceId: row.id,
    })
    const seedEnabled = !row.isSettled && Boolean(row.paymentMethod)
    setAutopayEnabled(seedEnabled)
    setAutopayCardId(
      seedEnabled ? row.paymentMethod?.stripePaymentMethodId ?? "" : ""
    )
    setManageInvoice(row)
  }

  const closeManage = () => {
    setManageInvoice(null)
    setAutopayEnabled(false)
    setAutopayCardId("")
  }

  const openSettle = (row) => {
    tracker.trackEvent(SETTLE_INVOICE_OPENED, {
      workOrderSequentialId,
      invoiceId: row.id,
    })
    setSettleInvoice(row)
  }

  const { mutate: saveAutopay, isLoading: isSavingAutopay } = useMutation(
    () =>
      updateInvoiceAutopay(
        marinaSlug,
        workOrderSequentialId,
        manageInvoice.id,
        {
          autopayEnabled,
          stripePaymentMethodId: autopayCardId,
        }
      ),
    {
      onSuccess: () => {
        tracker.trackEvent(INVOICE_AUTOPAY_SAVED, {
          workOrderSequentialId,
          invoiceId: manageInvoice?.id,
          autopayEnabled,
        })
        refreshPayments()
        showToast("Invoice Updated Successfully.", { type: "success" })
        closeManage()
      },
      onError: (error) => {
        tracker.trackEvent(INVOICE_AUTOPAY_SAVE_FAILED, {
          workOrderSequentialId,
          invoiceId: manageInvoice?.id,
        })
        showToast(error?.response?.data?.error || "Failed to update AutoPay.", {
          type: "error",
        })
      },
    }
  )

  const {
    invoiceToMerge,
    isMergingInvoice,
    onMergeInvoiceClick,
    onMergeInvoiceClose,
    onMergeSubmitSuccess,
  } = useMergeInvoice({
    resetInvoiceTxns: () => {},
    refreshItems: () => {},
    refreshPayments,
  })

  const openMerge = (row) => {
    tracker.trackEvent(MERGE_INVOICE_OPENED, {
      workOrderSequentialId,
      invoiceId: row.id,
    })
    onMergeInvoiceClick(row)
  }

  const autopayIncomplete =
    autopayEnabled &&
    (!autopayCardId || autopayCardId === ADD_NEW_PAYMENT_METHOD)

  const renderDetails = ({ isSettled }) => (
    <div className="flex flex-col gap-3">
      <InvoiceAutopayFields
        workOrderSequentialId={workOrderSequentialId}
        contactId={workOrder.contact?.id}
        enabled={autopayEnabled}
        onEnabledChange={setAutopayEnabled}
        stripePaymentMethodId={autopayCardId}
        onPaymentMethodChange={setAutopayCardId}
        woDefaultEnabled={Boolean(workOrder.autopayEnabled)}
        disabled={isSettled || !canManage}
      />
      {canManage && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            isLoading={isSavingAutopay}
            disabled={isSettled || autopayIncomplete}
            onClick={() => saveAutopay()}
          >
            Save AutoPay
          </Button>
        </div>
      )}
    </div>
  )

  return (
    <div className="overflow-x-auto md:overflow-visible">
      <PaymentsDataTable
        table={table}
        onManage={openManage}
        onSettle={openSettle}
        onMerge={openMerge}
        canManage={canManage}
      />

      <ManageInvoiceModal
        invoice={manageInvoice}
        isOpen={Boolean(manageInvoice)}
        onClose={closeManage}
        refreshPayments={refreshPayments}
        renderDetails={renderDetails}
        marinaSlug={marinaSlug}
        settleDisabled={!canManage}
        canManage={canManage}
        paymentMethods={paymentMethods}
        onReverseClick={() =>
          tracker.trackEvent(REVERSE_INVOICE_OPENED, {
            workOrderSequentialId,
            invoiceId: manageInvoice?.id,
          })
        }
      />

      <SettlePaymentModal
        payment={toLegacyInvoiceShape(settleInvoice)}
        paymentMethods={paymentMethods}
        isOpen={Boolean(settleInvoice)}
        onClose={() => setSettleInvoice(null)}
        onPaymentSettle={() => {
          setSettleInvoice(null)
          refreshPayments()
        }}
        afterLeave={() => setSettleInvoice(null)}
      />

      {/*
        TODO(DWA-191): merge candidates are limited to the current page of the
        paginated table; a WO with >25 invoices can't merge across pages. The
        full-list fetch lands with the toLegacyInvoiceShape retirement.
      */}
      <MergeInvoiceModal
        invoice={toLegacyInvoiceShape(invoiceToMerge)}
        availableInvoices={table.records.map(toLegacyInvoiceShape)}
        isOpen={isMergingInvoice}
        onClose={onMergeInvoiceClose}
        onMergeSubmitSuccess={onMergeSubmitSuccess}
      />
    </div>
  )
}

WorkOrderPayments.propTypes = {
  workOrder: PropTypes.shape({
    autopayEnabled: PropTypes.bool,
    canManagePayments: PropTypes.bool,
    contact: PropTypes.shape({ id: PropTypes.string }),
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default WorkOrderPayments
